/**
 * 納品件数(deliveries/{date})の読み書き。会社別・日別の手入力(現行「サマリ」シート相当)。
 *  - details: 会社ごとの内訳
 *      by_style: 品川／歩合／時給 の納品件数(by_appointer があればその合計)
 *      appointments: 取得件数の手修正(品川／歩合／時給)。無ければ日次集計(対応履歴)の値を使う
 *      by_appointer: アポインター別の納品件数(⑨指示。いずれ納品リスト突合で自動化)
 *      labor / commute: 人件費・交通費の入力(出勤形態×報酬区分)。未入力なら日次集計の値を使う
 *      client_appointments / cancel_*: 先方アポ数・キャンセル数
 *  - counts: 会社ごとの納品合計(details から計算。PL・会社別ページはこれを使う)
 * 売上実績 = 納品件数 × 会社単価。売上見込 = 取得件数 × 会社単価。
 */
import { collection, doc, documentId, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import { COST_STYLES, DELIVERY_STYLES, deliveryTotal, type CostStyle, type DateStr, type Delivery, type DeliveryDetail, type DeliveryStyle } from '@core/lib/types';

export async function fetchDelivery(date: DateStr): Promise<Delivery | null> {
  const snap = await getDoc(doc(getDb(), 'deliveries', date));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as object) } as Delivery;
}

/** from〜to(両端含む)の納品件数 */
export async function fetchDeliveries(from: DateStr, to: DateStr): Promise<Delivery[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'deliveries'), where(documentId(), '>=', from), where(documentId(), '<=', to), orderBy(documentId())),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Delivery);
}

function checkInt(v: number, label: string): number {
  if (!Number.isInteger(v) || v < 0) throw new Error(`${label}は0以上の整数で入力してください`);
  return v;
}

/** 内訳を検証して整える。アポインター別の入力があれば出勤形態別はその合計にする */
export function normalizeDetail(d: DeliveryDetail): DeliveryDetail {
  const by_style: Partial<Record<DeliveryStyle, number>> = {};
  for (const k of DELIVERY_STYLES) if (d.by_style[k] !== undefined) by_style[k] = checkInt(d.by_style[k]!, '納品件数');
  let by_appointer: DeliveryDetail['by_appointer'];
  if (d.by_appointer && Object.keys(d.by_appointer).length > 0) {
    by_appointer = {};
    const sums: Partial<Record<DeliveryStyle, number>> = {};
    for (const [opId, a] of Object.entries(d.by_appointer)) {
      by_appointer[opId] = { name: a.name, style: a.style, count: checkInt(a.count, `${a.name} の納品件数`) };
      sums[a.style] = (sums[a.style] ?? 0) + a.count;
    }
    for (const k of DELIVERY_STYLES) if (sums[k] !== undefined) by_style[k] = sums[k];
  }
  const cost = (src: Partial<Record<CostStyle, number>> | undefined, label: string) => {
    if (!src) return undefined;
    const out: Partial<Record<CostStyle, number>> = {};
    for (const k of COST_STYLES) if (src[k] !== undefined) out[k] = checkInt(src[k]!, label);
    return Object.keys(out).length > 0 ? out : undefined;
  };
  const labor = cost(d.labor, '人件費');
  const commute = cost(d.commute, '交通費');
  let appointments: Partial<Record<DeliveryStyle, number>> | undefined;
  if (d.appointments) {
    appointments = {};
    for (const k of DELIVERY_STYLES) if (d.appointments[k] !== undefined) appointments[k] = checkInt(d.appointments[k]!, '取得件数');
    if (Object.keys(appointments).length === 0) appointments = undefined;
  }
  return {
    by_style,
    ...(appointments ? { appointments } : {}),
    ...(by_appointer ? { by_appointer } : {}),
    ...(labor ? { labor } : {}),
    ...(commute ? { commute } : {}),
    client_appointments: checkInt(d.client_appointments ?? 0, '先方アポ数'),
    cancel_zaitaku: checkInt(d.cancel_zaitaku ?? 0, 'キャンセル数(在宅)'),
    cancel_shukkin: checkInt(d.cancel_shukkin ?? 0, 'キャンセル数(出勤)'),
  };
}

/** その日の納品件数・人件費・交通費を保存(会社ごとの内訳)。内訳が全て0の会社も保持する */
export async function saveDelivery(date: DateStr, details: Record<string, DeliveryDetail>, note: string, userId: string, before?: Delivery | null): Promise<void> {
  const cleanDetails: Record<string, DeliveryDetail> = {};
  const counts: Record<string, number> = {};
  for (const [cid, d] of Object.entries(details)) {
    cleanDetails[cid] = normalizeDetail(d);
    counts[cid] = deliveryTotal(cleanDetails[cid]);
  }
  await setDoc(doc(getDb(), 'deliveries', date), {
    date,
    counts,
    details: cleanDetails,
    note: note.trim() || null,
    updated_by: userId,
    updated_at: serverTimestamp(),
  });
  const changes: string[] = [];
  for (const [k, v] of Object.entries(counts)) {
    const prev = before?.counts?.[k] ?? 0;
    if (prev !== v) changes.push(`${k}: 納品 ${prev}件 → ${v}件`);
  }
  const sumCost = (det: Record<string, DeliveryDetail> | undefined, key: 'labor' | 'commute') =>
    Object.values(det ?? {}).reduce((s, d) => s + Object.values(d[key] ?? {}).reduce((a, b) => a + (b ?? 0), 0), 0);
  for (const key of ['labor', 'commute'] as const) {
    const prev = sumCost(before?.details, key);
    const next = sumCost(cleanDetails, key);
    if (prev !== next) changes.push(`${key === 'labor' ? '人件費' : '交通費'}: ${prev}円 → ${next}円`);
  }
  await writeAuditLog({ targetType: 'import', targetId: `deliveries/${date}`, action: 'delivery-update', by: userId, changes });
}
